import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { KeyRound, Loader2, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import SubscriptionSection from "@/components/SubscriptionSection";

export default function AccountPage() {
  const { user, refresh, logout } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [contactName, setContactName] = useState(user?.contact_name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    setName(user?.name || "");
    setContactName(user?.contact_name || "");
    setEmail(user?.email || "");
  }, [user?.email, user?.name, user?.contact_name]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/portal/account", {
        name,
        contact_name: contactName || null,
        email,
      });
      toast.success("Konto aktualisiert.");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (pwNew !== pwConfirm) {
      toast.error("Passwörter stimmen nicht überein.");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/portal/change-password", {
        current_password: pwCurrent,
        new_password: pwNew,
      });
      toast.success("Passwort geändert. Bitte erneut anmelden.");
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
      setTimeout(() => logout(), 800);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="portal-account-page">
      <header>
        <p className="label-eyebrow">Einstellungen</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Konto</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Passe deine Kontaktdaten und dein Passwort an.
        </p>
      </header>

      <form onSubmit={saveProfile} className="card-surface p-5 space-y-4" data-testid="account-profile-form">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Profil</h3>
        </div>
        <Separator className="bg-white/[0.06]" />
        <FormField label="Firmenname">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-background/50 h-11"
            data-testid="account-input-name"
            required
          />
        </FormField>
        <FormField label="Ansprechpartner">
          <Input
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            className="bg-background/50 h-11"
            data-testid="account-input-contact"
          />
        </FormField>
        <FormField label="E-Mail">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-background/50 h-11"
            data-testid="account-input-email"
            required
          />
        </FormField>
        <div>
          <Button
            type="submit"
            className="rounded-full h-11 px-5 gap-2"
            disabled={savingProfile}
            data-testid="account-save-btn"
          >
            {savingProfile ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Speichern
          </Button>
        </div>
      </form>

      <form onSubmit={changePw} className="card-surface p-5 space-y-4" data-testid="account-password-form">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Passwort ändern</h3>
        </div>
        <Separator className="bg-white/[0.06]" />
        <FormField label="Aktuelles Passwort">
          <Input
            type="password"
            value={pwCurrent}
            onChange={(e) => setPwCurrent(e.target.value)}
            required
            className="bg-background/50 h-11"
            data-testid="account-current-password"
            autoComplete="current-password"
          />
        </FormField>
        <FormField label="Neues Passwort" hint="Mindestens 8 Zeichen.">
          <Input
            type="password"
            value={pwNew}
            onChange={(e) => setPwNew(e.target.value)}
            minLength={8}
            required
            className="bg-background/50 h-11"
            data-testid="account-new-password"
            autoComplete="new-password"
          />
        </FormField>
        <FormField label="Neues Passwort bestätigen">
          <Input
            type="password"
            value={pwConfirm}
            onChange={(e) => setPwConfirm(e.target.value)}
            minLength={8}
            required
            className="bg-background/50 h-11"
            data-testid="account-confirm-password"
            autoComplete="new-password"
          />
        </FormField>
        <div>
          <Button
            type="submit"
            className="rounded-full h-11 px-5 gap-2"
            disabled={savingPw || !pwCurrent || !pwNew || pwNew.length < 8}
            data-testid="account-change-password-btn"
          >
            {savingPw ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <KeyRound className="w-4 h-4" />
            )}
            Passwort aktualisieren
          </Button>
        </div>
      </form>

      <div className="card-surface p-5" data-testid="account-subscription-card">
        <SubscriptionSection />
      </div>
    </div>
  );
}

function FormField({ label, hint, children }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <div className="mt-2">{children}</div>
      {hint ? <p className="text-xs text-muted-foreground mt-1.5">{hint}</p> : null}
    </div>
  );
}
